import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { calendarModuleForBrand } from '../lib/calendar-module';
import { Badge, Button, Card, ErrorAlert, PageHeader, Spinner } from '../components/ui';

const DOW = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Ndz'];
type CalendarView = 'day' | 'week' | 'month';

export function CalendarPage() {
  const [view, setView] = useState<CalendarView>('month');
  const [cursor, setCursor] = useState(() => new Date());
  const { from, to, label } = useMemo(() => calendarRange(view, cursor), [view, cursor]);
  const { data, error, loading } = useAsync(() => api.calendar(from, to), [from, to]);
  const { data: videoSchedule, error: videoScheduleError } = useAsync(() => api.fbo.videoSchedule(from, to), [from, to]);

  const { data: brands } = useAsync(() => api.brands.list(), []);
  const [planBrand, setPlanBrand] = useState('');
  const plans = useAsync(() => api.planner.plans(planBrand || undefined), [planBrand]);
  const [planning, setPlanning] = useState(false);
  const [videoDates, setVideoDates] = useState<Record<string, string>>({});
  const [videoSaving, setVideoSaving] = useState<string | null>(null);
  const [planError, setPlanError] = useState<unknown>(null);
  const [planNote, setPlanNote] = useState<string | null>(null);

  const runPlan = async () => {
    const bid = planBrand || brands?.[0]?.id || '';
    if (!bid) return;
    setPlanning(true);
    setPlanError(null);
    setPlanNote(null);
    try {
      const res = await api.planner.plan(bid);
      setPlanNote(`Zaplanowano ${res.plan.items.length} pozycji · ${res.ideasCreated} pomysłów w Ideas.`);
      plans.reload();
    } catch (err) {
      setPlanError(err);
    } finally {
      setPlanning(false);
    }
  };
  const rescheduleVideo = async (jobId: string) => {
    const value = videoDates[jobId];
    if (!value) return;
    setVideoSaving(jobId);
    try { await api.fbo.scheduleVideo(jobId, new Date(value).toISOString()); } catch (err) { setPlanError(err); } finally { setVideoSaving(null); }
  };

  const byDay = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>['items']>();
    for (const item of data?.items ?? []) {
      const key = item.scheduledAt.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return map;
  }, [data]);

  const cells = useMemo(() => buildMonthCells(cursor), [cursor]);
  const week = useMemo(() => buildWeekDays(cursor), [cursor]);
  const move = (direction: number) => {
    setCursor((current) => {
      const next = new Date(current);
      if (view === 'day') next.setDate(next.getDate() + direction);
      else if (view === 'week') next.setDate(next.getDate() + direction * 7);
      else next.setMonth(next.getMonth() + direction);
      return next;
    });
  };
  const renderItem = (item: NonNullable<typeof data>['items'][number]) => {
    const module = calendarModuleForBrand(item.brandSlug, item.brandName);
    return <Link key={`${item.id}-${item.scheduledAt}`} to={`/content/${item.contentId}`} className={`calendar__item calendar__item--${module.key}`} title={`${module.label} · ${item.topic}`} style={{ display: 'block', textDecoration: 'none', padding: '7px 8px', marginTop: 5, borderRadius: 7, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
        <strong style={{ fontSize: 12 }}>{new Date(item.scheduledAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</strong>
        <Badge tone={item.status === 'SENT' ? 'success' : item.status === 'FAILED' ? 'danger' : 'info'}>{item.status}</Badge>
      </div>
      <div className="calendar__module-label">{module.label}</div>
      <div style={{ fontSize: 11, marginTop: 3 }}><b>{item.platform}</b> · {item.brandName}</div>
      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 3, lineHeight: 1.25 }}>{item.topic}</div>
    </Link>;
  };

  return (
    <>
      <PageHeader
        title="Calendar"
        subtitle="Zatwierdzony content przypisany do daty. Wybierz widok i otwórz materiał bezpośrednio z kalendarza."
        actions={
          <>
            <div className="btn-row">
              {(['day', 'week', 'month'] as CalendarView[]).map((option) => <Button key={option} size="sm" variant={view === option ? 'primary' : 'default'} onClick={() => setView(option)}>{option === 'day' ? 'Dzień' : option === 'week' ? 'Tydzień' : 'Miesiąc'}</Button>)}
            </div>
            <Button size="sm" onClick={() => move(-1)}>
              ← Poprzedni
            </Button>
            <span style={{ fontWeight: 600, padding: '0 6px' }}>{label}</span>
            <Button size="sm" onClick={() => move(1)}>
              Następny →
            </Button>
          </>
        }
      />
      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}

      <Card title="Planner tygodniowy (auto)">
        <div className="form-row">
          <select className="select" value={planBrand} onChange={(e) => setPlanBrand(e.target.value)}>
            <option value="">— wszystkie marki —</option>
            {brands?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <Button variant="primary" onClick={runPlan} disabled={planning || !brands?.length}>
            {planning ? 'Planowanie…' : 'Zaplanuj tydzień'}
          </Button>
        </div>
        <ErrorAlert error={planError} />
        {planNote && <div className="alert alert--success" style={{ marginTop: 10 }}>{planNote}</div>}
        {plans.loading && <Spinner />}
        {plans.error ? <ErrorAlert error={plans.error} /> : null}
        {plans.data && plans.data.length > 0 && (
          <div className="stack" style={{ marginTop: 12 }}>
            {plans.data.map((p) => (
              <details key={p.id}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  {p.periodStart} → {p.periodEnd} · {p.items.length} pozycji
                </summary>
                <table className="table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>Dzień</th>
                      <th>Platforma</th>
                      <th>Kind</th>
                      <th>Temat</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.items.map((it) => (
                      <tr key={it.id}>
                        <td>{it.day}</td>
                        <td>
                          <Badge tone="neutral">{it.platform}</Badge>
                        </td>
                        <td>
                          <Badge tone="info">{it.kind}</Badge>
                        </td>
                        <td>{it.topic ?? '—'}</td>
                        <td>
                          <Badge tone={it.status === 'GENERATED' ? 'success' : 'neutral'}>{it.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            ))}
          </div>
        )}
      </Card>
      <div style={{ height: 16 }} />

      <Card title={`${view === 'day' ? 'Widok dnia' : view === 'week' ? 'Widok tygodnia' : 'Widok miesiąca'} · materiały (${data?.items.length ?? 0})`}>
        <div className="calendar__legend" aria-label="Kolory według modułu">
          {[
            ['bloodcrimes', 'BloodCrimes'], ['fbo', 'FBO Studio'], ['riftcells', 'RiftCells'], ['budzetio', 'Budżetio'], ['other', 'Inne'],
          ].map(([key, label]) => <span key={key} className={`calendar__legend-item calendar__legend-item--${key}`}><i aria-hidden="true" />{label}</span>)}
        </div>
        {view === 'month' && <div className="calendar">
          {DOW.map((d) => <div key={d} className="calendar__dow">{d}</div>)}
          {cells.map((cell) => <div key={cell.key} className={`calendar__cell${cell.inMonth ? '' : ' calendar__cell--muted'}`}>
            <div className="calendar__day">{cell.day}</div>
            {(byDay.get(cell.key) ?? []).map(renderItem)}
          </div>)}
        </div>}
        {view === 'week' && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))', gap: 8, overflowX: 'auto' }}>
          {week.map((day) => <div key={day.key} className="calendar__cell" style={{ minHeight: 260 }}><div className="calendar__day">{day.label}</div>{(byDay.get(day.key) ?? []).map(renderItem)}</div>)}
        </div>}
        {view === 'day' && <div className="stack">
          {(byDay.get(isoDay(cursor)) ?? []).length === 0 && <p className="muted">Brak zaplanowanych materiałów tego dnia.</p>}
          {(byDay.get(isoDay(cursor)) ?? []).map(renderItem)}
        </div>}
      </Card>
      <div style={{ height: 16 }} />
      <Card title="FBO Video Schedule">
        <ErrorAlert error={videoScheduleError} />
        {!videoSchedule?.length && <p className="muted">Brak zaplanowanych video FBO.</p>}
        {videoSchedule?.length ? <table className="table"><thead><tr><th>Data</th><th>Platforma</th><th>Status</th><th>Job</th><th>Przesuń</th></tr></thead><tbody>{videoSchedule.map((item) => <tr key={item.id}><td>{new Date(item.scheduled_at).toLocaleString('pl-PL')}</td><td><Badge>{item.platform ?? 'MASTER'}</Badge></td><td><Badge tone={item.status === 'PENDING' ? 'warning' : item.status === 'FAILED' ? 'danger' : 'success'}>{item.status}</Badge></td><td className="mono">{item.video_job_id}</td><td><div className="btn-row"><input className="input" type="datetime-local" value={videoDates[item.video_job_id] ?? toLocalInput(item.scheduled_at)} onChange={(e) => setVideoDates((current) => ({ ...current, [item.video_job_id]: e.target.value }))} /><Button size="sm" disabled={videoSaving !== null || item.status !== 'PENDING'} onClick={() => rescheduleVideo(item.video_job_id)}>{videoSaving === item.video_job_id ? 'Zapisuję…' : 'Zapisz'}</Button></div></td></tr>)}</tbody></table> : null}
      </Card>
    </>
  );
}

function calendarRange(view: CalendarView, cursor: Date): { from: string; to: string; label: string } {
  let start: Date;
  let end: Date;
  if (view === 'day') {
    start = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
    end = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), 23, 59, 59);
  } else if (view === 'week') {
    const monday = new Date(cursor);
    monday.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
    start = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
    end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6, 23, 59, 59);
  } else {
    start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59);
  }
  const label = view === 'day'
    ? start.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : view === 'week'
      ? `${start.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : start.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
  return { from: start.toISOString(), to: end.toISOString(), label };
}

interface Cell {
  key: string;
  day: number;
  inMonth: boolean;
}

function buildMonthCells(anchor: Date): Cell[] {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7; // Monday-first
  const cells: Cell[] = [];
  const start = new Date(year, month, 1 - startWeekday);
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const key = isoDay(d);
    cells.push({ key, day: d.getDate(), inMonth: d.getMonth() === month });
  }
  return cells;
}

function buildWeekDays(anchor: Date): { key: string; label: string }[] {
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - ((anchor.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + index);
    return { key: isoDay(day), label: `${DOW[index]} · ${day.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}` };
  });
}

function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
